import { useState } from 'react';
import { NavLink, useLocation } from 'react-router';
import { Ellipsis } from 'lucide-react';
import type { NavGroup } from '@/lib/nav/types';
import { isNavActive, MOBILE_TAB_IDS } from '@/lib/nav/staff';
import { cn } from '@/lib/cn';
import { useI18n } from '@/lib/i18n';
import { Sheet } from '@/components/ui/Dialog';
import { LibrarySwitcher } from './LibrarySwitcher';

/** Bottom tab bar (4 primary destinations + More) for phones and small tablets. */
export function MobileTabBar({ groups }: { groups: readonly NavGroup[] }) {
  const { pathname } = useLocation();
  const { t } = useI18n();
  const [moreOpen, setMoreOpen] = useState(false);
  const all = groups.flatMap((g) => g.items);
  const tabs = MOBILE_TAB_IDS.map((id) => all.find((i) => i.id === id)).filter((i): i is NonNullable<typeof i> => Boolean(i));
  const moreActive = !tabs.some((tab) => isNavActive(pathname, tab.to, tab.end));

  return (
    <>
      <nav aria-label={t('nav.main')} className="no-print glass-strong fixed inset-x-0 bottom-0 z-40 border-t border-line pb-[env(safe-area-inset-bottom)] lg:hidden">
        <ul className="mx-auto flex max-w-xl items-stretch justify-around px-2">
          {tabs.map((tab) => {
            const active = isNavActive(pathname, tab.to, tab.end);
            return (
              <li key={tab.id} className="flex-1">
                <NavLink to={tab.to} end={tab.end} aria-current={active ? 'page' : undefined} className={cn('flex flex-col items-center gap-1 py-2.5 text-[0.6875rem] font-medium transition-colors', active ? 'text-sapphire-700 dark:text-sapphire-300' : 'text-ink-muted')}>
                  <span className={cn('grid h-7 w-12 place-items-center rounded-full transition', active && 'bg-sapphire-100 dark:bg-sapphire-900/60')}>
                    <tab.icon className="h-[1.125rem] w-[1.125rem]" aria-hidden="true" />
                  </span>
                  {tab.label}
                </NavLink>
              </li>
            );
          })}
          <li className="flex-1">
            <button type="button" aria-haspopup="dialog" onClick={() => setMoreOpen(true)} className={cn('flex w-full flex-col items-center gap-1 py-2.5 text-[0.6875rem] font-medium', moreActive ? 'text-sapphire-700 dark:text-sapphire-300' : 'text-ink-muted')}>
              <span className={cn('grid h-7 w-12 place-items-center rounded-full', moreActive && 'bg-sapphire-100 dark:bg-sapphire-900/60')}>
                <Ellipsis className="h-[1.125rem] w-[1.125rem]" aria-hidden="true" />
              </span>
              {t('nav.more')}
            </button>
          </li>
        </ul>
      </nav>
      <Sheet open={moreOpen} onClose={() => setMoreOpen(false)} title={t('nav.more')} description={t('nav.staffConsole')}>
        <div className="mb-5 md:hidden">
          <LibrarySwitcher />
        </div>
        <div className="space-y-6">
          {groups.map((group) => (
            <section key={group.id} aria-labelledby={`more-${group.id}`}>
              <h3 id={`more-${group.id}`} className="mb-2 text-2xs font-semibold uppercase tracking-wider text-ink-subtle">
                {group.label}
              </h3>
              <ul className="grid grid-cols-2 gap-2">
                {group.items.map((item) =>
                  item.children && item.children.length > 1 ? (
                    <li key={item.id} className="col-span-2 rounded-xl border border-line bg-surface p-3">
                      <NavLink to={item.to} end onClick={() => setMoreOpen(false)} className="flex items-center gap-2.5 text-sm font-semibold text-ink">
                        <item.icon className="h-[1.125rem] w-[1.125rem] shrink-0 text-sapphire-600 dark:text-sapphire-300" aria-hidden="true" />
                        {item.label}
                      </NavLink>
                      <ul className="mt-2.5 flex flex-wrap gap-1.5">
                        {item.children.map((child) => (
                          <li key={child.id}>
                            <NavLink to={child.to} end={child.end} onClick={() => setMoreOpen(false)} className="block rounded-full bg-sunken px-3 py-1.5 text-xs font-medium text-ink-muted ring-1 ring-inset ring-line active:bg-sapphire-50">
                              {child.label}
                            </NavLink>
                          </li>
                        ))}
                      </ul>
                    </li>
                  ) : (
                    <li key={item.id}>
                      <NavLink to={item.to} end={item.end} onClick={() => setMoreOpen(false)} className="flex items-center gap-2.5 rounded-xl border border-line bg-surface px-3 py-3 text-sm font-medium text-ink transition active:scale-[0.98]">
                        <item.icon className="h-[1.125rem] w-[1.125rem] shrink-0 text-sapphire-600 dark:text-sapphire-300" aria-hidden="true" />
                        <span className="truncate">{item.label}</span>
                      </NavLink>
                    </li>
                  ),
                )}
              </ul>
            </section>
          ))}
        </div>
      </Sheet>
    </>
  );
}
